import React, { useState, useRef } from 'react';
import { ZapparCamera, InstantTracker, ZapparCanvas } from '@zappar/zappar-react-three-fiber';
import Diamond from './Diamond';
import {
  useGLTF,
  Caustics,
  CubeCamera,
  Environment,
  RandomizedLight,
  AccumulativeShadows,
  MeshRefractionMaterial,
  MeshTransmissionMaterial
} from '@react-three/drei'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import { Leva } from 'leva'

const colors = [
  { name: 'Gold', value: '#FFD700' },
  { name: 'Silver', value: '#C0C0C0' },
  { name: 'Platinum', value: '#E5E4E2' },
  { name: 'Rose Gold', value: '#B76E79' }
];

const diamondColors = [
  { name: 'Clear', value: '#FFFFFF' },
  { name: 'Champagne', value: '#F4E27E' },
  { name: 'Pink', value: '#F6C1CB' },
  { name: 'Blue', value: '#A3C1DA' }
];

// Wrapper component to enable tap/drag to rotate the object
function DraggableRing({ children }) {
  const groupRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const previousX = useRef(0);

  const handlePointerDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    previousX.current = e.clientX;
    e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !groupRef.current) return;
    e.stopPropagation();
    const deltaX = e.clientX - previousX.current;
    
    // Spin the ring along Y axis (adjust multiplier for sensitivity)
    groupRef.current.rotation.y += deltaX * 0.015;
    
    previousX.current = e.clientX;
  };

  const handlePointerUp = (e) => {
    e.stopPropagation();
    setIsDragging(false);
    if (e.target.hasPointerCapture(e.pointerId)) {
      e.target.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <group 
      ref={groupRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerOut={handlePointerUp}
    >
      {children}
    </group>
  );
}

function App() {
  let [placementMode, setPlacementMode] = useState(true);

  // Track the currently selected ring core color
  const [ringColor, setRingColor] = useState('#FFD700');
  const [hoverColorName, setHoverColorName] = useState('');

  // Track the currently selected diamond color
  const [diamondColor, setDiamondColor] = useState('#FFFFFF');
  const [hoverDiamondColorName, setHoverDiamondColorName] = useState('');

  // Determine the name of the currently active color for display
  const activeColorName = colors.find(c => c.value === ringColor)?.name;
  const activeDiamondColorName = diamondColors.find(c => c.value === diamondColor)?.name;

  return (
    <>
      <Leva hidden />
      <ZapparCanvas>
        <ZapparCamera />
        <InstantTracker placementMode={placementMode} placementCameraOffset={[0, 0, -5]}>
          <ambientLight intensity={0.5 * Math.PI} />
          <spotLight decay={0} position={[5, 5, -10]} angle={0.15} penumbra={1} />
          <pointLight decay={0} position={[-10, -10, -10]} />
          <DraggableRing>
            <Diamond ringColor={ringColor} diamondColor={diamondColor} />
          </DraggableRing>
          {/* <mesh>
            <sphereBufferGeometry />
            <meshStandardMaterial color="pink" />
          </mesh> */}
        </InstantTracker>
        {/* <directionalLight position={[2.5, 8, 5]} intensity={1.5} /> */}
        {/* <AccumulativeShadows */}
        {/* temporal
          frames={100}
          color="orange"
          colorBlend={2}
          toneMapped={true}
          alphaTest={0.7}
          opacity={1}
          scale={12}
          position={[0, -0.5, 0]}> */}
        <RandomizedLight amount={8} radius={10} ambient={0.5} position={[5, 5, -10]} bias={0.001} />
        {/* </AccumulativeShadows> */}
        <Environment files="https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/aerodynamics_workshop_1k.hdr" />
        <EffectComposer>
          <Bloom luminanceThreshold={1} intensity={1} levels={10} mipmapBlur />
        </EffectComposer>
      </ZapparCanvas>
      
      {/* Configurator UI */}
      <div className="config-wrapper" style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '40px',
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        padding: '20px 40px',
        fontFamily: '"Inter", "Segoe UI", sans-serif',
        zIndex: 1000
      }}>
        {/* RING BASE CONFIGURATOR */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', color: '#555', gap: '8px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 7a7 7 0 1 0 8 0" />
              <circle cx="12" cy="7" r="1.5" fill="currentColor" stroke="none" opacity="0.3" />
            </svg>
            <span style={{ fontWeight: '500', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Ring Base <span style={{ opacity: 0.5, margin: '0 5px' }}>|</span> <strong style={{ color: '#111' }}>{hoverColorName || activeColorName}</strong>
            </span>
          </div>

          <div className="swatch-group" style={{ display: 'flex', gap: '20px' }}>
            {colors.map((c) => (
              <div
                key={c.name}
                className="config-swatch"
                onClick={() => setRingColor(c.value)}
                onMouseEnter={() => setHoverColorName(c.name)}
                onMouseLeave={() => setHoverColorName('')}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: c.value,
                  cursor: 'pointer',
                  border: ringColor === c.value ? '4px solid #fff' : '3px solid transparent',
                  outline: ringColor === c.value ? '2px solid #ccc' : 'none',
                  transform: hoverColorName === c.name ? 'scale(1.1)' : 'scale(1)',
                  transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                }}
              />
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="config-divider" style={{ width: '1px', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '1px' }}></div>

        {/* DIAMOND CONFIGURATOR */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', color: '#555', gap: '8px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3h12l4 6-10 13L2 9z" />
              <path d="M11 3 8 9l4 13 4-13-3-6" />
              <path d="M2 9h20" />
            </svg>
            <span style={{ fontWeight: '500', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Diamond <span style={{ opacity: 0.5, margin: '0 5px' }}>|</span> <strong style={{ color: '#111' }}>{hoverDiamondColorName || activeDiamondColorName}</strong>
            </span>
          </div>

          <div className="swatch-group" style={{ display: 'flex', gap: '20px' }}>
            {diamondColors.map((c) => (
              <div
                key={c.name}
                className="config-swatch"
                onClick={() => setDiamondColor(c.value)}
                onMouseEnter={() => setHoverDiamondColorName(c.name)}
                onMouseLeave={() => setHoverDiamondColorName('')}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: c.value,
                  cursor: 'pointer',
                  border: diamondColor === c.value ? '4px solid #fff' : '3px solid transparent',
                  outline: diamondColor === c.value ? '2px solid #ccc' : 'none',
                  transform: hoverDiamondColorName === c.name ? 'scale(1.1)' : 'scale(1)',
                  transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div
        id="zappar-placement-ui"
        onClick={() => {
          setPlacementMode((currentPlacementMode) => !currentPlacementMode);
        }}
        onKeyDown={() => {
          setPlacementMode((currentPlacementMode) => !currentPlacementMode);
        }}
        role="button"
        tabIndex={0}
      >
        Tap here to
        {placementMode ? ' place ' : ' pick up '}
        the object
      </div>
    </>
  );
}

export default App;
